import { Drawer, PathBuilder, StyleForMarker, Svg, TextLine } from "@common/components/Rendering";
import {
  ArrowId,
  axis_label_style,
  ColumnMarkers,
  interaction_curve_caption,
  interaction_curve_color_accidental,
  interaction_curve_color_fire,
  interaction_curve_color_seismic,
  interaction_graph_line,
  interaction_point_style,
  interaction_selected_lcomb_text,
  interaction_style_extended_selected,
  interaction_style_fail,
  interaction_style_ok,
  interaction_tick_horizontal,
  interaction_tick_label,
  interaction_tick_style,
  interaction_tick_vertical,
  interaction_zero_line,
} from "../ViewportStyles";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";
import { selectProject } from "../../features/projects/projectSlice";
import { useDevConfig } from "../../App";
import { useAutoZoomViewport } from "../../features/user/userSlice";
import { ng } from "@common/ngds";
import { isSlsType, isUlsType } from "@common/helpers/funlib";
import { DiagramResults } from "./drawSectionForceDiagrams";
import { IDesignResult, ISectionForce } from "@common/types/sharedTypes";

interface Data {
  force: number;
  moment: number;
}

const nice_step = (span: number, targetCount: number = 5): number => {
  const rough = span / targetCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  const nice = normalized < 1.5 ? 1 : normalized < 3.5 ? 2 : normalized < 7.5 ? 5 : 10;
  return nice * magnitude;
};

// Moment control types that are relevant for the interaction diagram, per drawn direction.
// The engine reports either the split positive/negative controls or the merged "max of both"
// control (CONTROL_TYPE_CONCRETE_MOMENT_M*), so accept both spellings — matching only the split
// ones leaves every load combination without a dominant control, and the diagram loses its points.
const MOMENT_CONTROLS_M1 = new Set<number>([
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_POSITIVE_MOMENT_M1,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_NEGATIVE_MOMENT_M1,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_MOMENT_M1,
]);

const MOMENT_CONTROLS_M2 = new Set<number>([
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_POSITIVE_MOMENT_M2,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_NEGATIVE_MOMENT_M2,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_MOMENT_M2,
]);

/** The moment control with the highest utilisation among `types`, or null if none is reported. */
const findDominantControl = (results: IDesignResult[], types: Set<number>): IDesignResult | null =>
  results
    .filter((r) => types.has(Number(r.check)))
    .reduce<IDesignResult | null>((best, r) => (best === null || r.utilisation > best.utilisation ? r : best), null);

// Keys of the named quantities a moment control reports in its `values` map (the same values its
// MathML formula is written with).
const VALUE_NORMAL_FORCE = "N";
const VALUE_DESIGN_MOMENT = "MEd";

/**
 * The design point a moment control was checked at: the normal force and the DESIGN moment MEd,
 * which is the moment from load M plus the additional moment Madd from second-order effects.
 *
 * This is what belongs on the interaction diagram. The analysis section forces carry M alone - for
 * a concrete column the additional moment is not an analysis quantity at all, it is produced by the
 * code check itself (nominal stiffness / nominal curvature), so a marker placed from section forces
 * sits exactly Madd short of the point whose utilisation it is labelled with.
 *
 * Values are SI (N, Nm), like every other force in this file, and both are signed with the same
 * convention the plot uses - compression negative - so neither needs flipping. That is unlike the
 * curve's own normalForce, which is positive in compression and negated where it is read.
 *
 * Returns null when the engine reported no value map, or not these keys - the caller then falls
 * back to section forces.
 */
const designPointFromControl = (control: IDesignResult): { force: number; moment: number } | null => {
  const values = control.values;
  if (!values) return null;
  const force = values[VALUE_NORMAL_FORCE];
  const moment = values[VALUE_DESIGN_MOMENT];
  if (!Number.isFinite(force) || !Number.isFinite(moment)) return null;
  return { force, moment };
};

/** Find the section force entry closest to targetPos along the element. */
const findClosestForce = (positions: ISectionForce[], targetPos: number): ISectionForce | null => {
  if (!positions || positions.length === 0) return null;
  let best = positions[0];
  let bestDist = Math.abs(positions[0].pos - targetPos);
  for (const p of positions) {
    const dist = Math.abs(p.pos - targetPos);
    if (dist < bestDist) {
      bestDist = dist;
      best = p;
    }
  }
  return best;
};

/** A curve is drawable only when the engine actually filled it with points. */
const curveHasPoints = (
  curve: ng.result.interaction_diagram.Curve_json | undefined | null,
): curve is ng.result.interaction_diagram.Curve_json =>
  !!curve && !!curve.moment && (curve.normalForce?.length ?? 0) > 0;

/** The interaction diagram a load combination result carries, or null when it carries none. */
const diagramOfLcomb = (
  lcomb: ng.output.LoadCombinationOutput_json | undefined | null,
): ng.result.interaction_diagram.Result_json | null => (lcomb?.interactionDiagrams ?? [])[0] ?? null;

/** A diagram is worth drawing once either uniaxial curve has been filled with points. */
const diagramIsDrawable = (
  result: ng.result.interaction_diagram.Result_json | null | undefined,
): result is ng.result.interaction_diagram.Result_json =>
  curveHasPoints(result?.yAxis) || curveHasPoints(result?.zAxis);

/**
 * The type of each load combination, keyed by guid.
 *
 * The type lives on the input combination, not on its result - the result only carries the guid.
 */
const lcombTypesByGuid = (
  projectData: ng.frontend.column.Project_json | null | undefined,
): Map<string, ng.loadcombination.Type> => {
  const combinations: ng.loadcombination.Data_json[] =
    projectData?.data?.input?.structures?.[0]?.loading?.combinations ?? [];
  const types = new Map<string, ng.loadcombination.Type>();
  for (const lc of combinations) {
    const guid = lc?.id?.guid;
    if (!guid) continue;
    // Guard the missing type ourselves - Type_FromObject logs an "unhandled value" for it.
    types.set(
      guid,
      lc.type == null ? ng.loadcombination.Type.TYPE_UNSPECIFIED : ng.loadcombination.Type_FromObject(lc.type),
    );
  }
  return types;
};

/** What the diagram draws for the current selection. */
interface InteractionSelection {
  /** The curve set to draw - one interaction diagram, belonging to one load combination type. */
  result: ng.result.interaction_diagram.Result_json;
  /** Only load combinations of this type may contribute a marker. */
  markerType: ng.loadcombination.Type;
}

/**
 * The curve set for `selectedLcombGuid`, plus the load combination type whose markers may be drawn
 * on it - or null when the selection has no interaction diagram to show at all.
 *
 * The engine returns one interaction diagram per load combination, and the curve depends on the
 * material and geometrical values the combination is checked with: a TYPE_FIRE combination is
 * checked with reduced material properties on a reduced section, a TYPE_ULTIMATE one is not. Points
 * from two types therefore sit on two different curves, and mixing their markers onto the one curve
 * on screen would put a marker beside a boundary that never governed it. So the diagram drawn is the
 * selected combination's own, and the markers are its own plus those of its same-type siblings -
 * which share its curve, and so only need one of them drawn.
 *
 * Returns null for an SLS selection (characteristic, frequent, quasi-permanent): those carry no
 * interaction check, and the panels are hidden for them rather than showing another type's curve.
 */
const resolveInteraction = (
  projectData: ng.frontend.column.Project_json | null | undefined,
  selectedLcombGuid: string,
): InteractionSelection | null => {
  const lcombs = projectData?.data?.output?.lcombs ?? [];
  const types = lcombTypesByGuid(projectData);
  const typeOf = (guid: string | undefined): ng.loadcombination.Type =>
    types.get(guid ?? "") ?? ng.loadcombination.Type.TYPE_UNSPECIFIED;

  if (isSlsType(typeOf(selectedLcombGuid))) return null;

  const drawableOfType = (markerType: ng.loadcombination.Type): InteractionSelection | null => {
    for (const lcomb of lcombs) {
      if (typeOf(lcomb.lcombGuid) !== markerType) continue;
      const result = diagramOfLcomb(lcomb);
      if (diagramIsDrawable(result)) return { result, markerType };
    }
    return null;
  };

  const selected = lcombs.find((lc) => lc.lcombGuid === selectedLcombGuid);
  if (selected) {
    const markerType = typeOf(selected.lcombGuid);
    const own = diagramOfLcomb(selected);
    // The selection's own diagram is the one to draw; a same-type sibling's is the same curve, so it
    // stands in when the selection itself came back without one.
    if (diagramIsDrawable(own)) return { result: own, markerType };
    return drawableOfType(markerType);
  }

  // No load combination result under that guid: an envelope selection, or one made before the
  // results were in. Nothing names a type to prefer, so take the first ULS combination that has a
  // diagram and show that type's markers - one type's, never a mix.
  for (const lcomb of lcombs) {
    const markerType = typeOf(lcomb.lcombGuid);
    if (!isUlsType(markerType)) continue;
    const result = diagramOfLcomb(lcomb);
    if (diagramIsDrawable(result)) return { result, markerType };
  }
  return null;
};

/**
 * How a curve is marked as belonging to a load combination type other than the ultimate one: the
 * colour it is drawn in, and the caption naming the type beside it.
 *
 * TYPE_ULTIMATE returns null, and so does anything untyped. Ultimate is the standard case - the
 * curve most diagrams show - so it stays the neutral grey it has always been and carries no
 * caption. A coloured, captioned curve therefore always means the same thing: what is on screen is
 * NOT the ultimate boundary, and its capacity was found with that type's own materials and section.
 *
 * `captionKey` is the existing translation key for the type's name; `captionFallback` is what
 * i18next shows where the key is missing (there is no seismic_ key yet).
 */
const curveAccent = (
  type: ng.loadcombination.Type,
): { color: string; captionKey: string; captionFallback: string } | null => {
  switch (type) {
    case ng.loadcombination.Type.TYPE_FIRE:
      return { color: interaction_curve_color_fire, captionKey: "fire_.label", captionFallback: "Fire" };
    case ng.loadcombination.Type.TYPE_ACCIDENTAL:
      return {
        color: interaction_curve_color_accidental,
        captionKey: "accident_.label",
        captionFallback: "Accidental",
      };
    case ng.loadcombination.Type.TYPE_SEISMIC:
      return { color: interaction_curve_color_seismic, captionKey: "seismic_.label", captionFallback: "Seismic" };
    default:
      return null;
  }
};

/**
 * True when a code check has produced interaction curves to draw for the selected load combination.
 * The panels are hidden until then, so the user never sees an empty diagram with nothing but axes -
 * and they stay hidden for an SLS selection, which has no interaction check at all (see
 * isSlsLcombSelection).
 */
export const hasInteractionDiagramData = (
  projectData: ng.frontend.column.Project_json | null | undefined,
  selectedLcombGuid: string,
): boolean => resolveInteraction(projectData, selectedLcombGuid) !== null;

/**
 * True when the selected load combination is an SLS one (characteristic, frequent,
 * quasi-permanent). There is no interaction check in the serviceability limit state, so the whole
 * diagram is hidden for such a selection - including while it is the maximized panel.
 */
export const isSlsLcombSelection = (
  projectData: ng.frontend.column.Project_json | null | undefined,
  selectedLcombGuid: string,
): boolean => isSlsType(lcombTypesByGuid(projectData).get(selectedLcombGuid));

export const InteractionDiagram = (props: {
  alternate: boolean;
  results: DiagramResults;
  selectedLcombGuid: string;
}) => {
  const isDev = useDevConfig();
  const autoZoom = useAutoZoomViewport();
  const g = new Drawer<ArrowId>();
  const project = useAppSelector(selectProject);
  const { t } = useTranslation();

  const selection = resolveInteraction(project?.data, props.selectedLcombGuid);
  const firstResult = selection?.result ?? null;
  const lcombTypes = lcombTypesByGuid(project?.data);

  // Null for an ultimate (or untyped) selection - that curve is drawn exactly as before.
  const accent = selection ? curveAccent(selection.markerType) : null;
  const curve_line = accent ? { ...interaction_graph_line, lineColor: accent.color } : interaction_graph_line;

  // config
  const show_curve_points = false;
  const force_scale = 1;
  const moment_scale = 3;
  const moment_extra = 20;
  const force_pos_extra = 50;
  const force_neg_extra = 50;
  const tick_offset = 7;
  const allowBiaxial = false; // whether to show biaxial interaction curve when available (dashed line)

  // Section forces are in SI (N, Nm); interaction diagram API also returns N/Nm
  const N_TO_KN = 1 / 1000;
  const NM_TO_KNM = 1 / 1000;

  const buildCurveFromReal = (curve: ng.result.interaction_diagram.Curve_json | undefined): Data[] | null => {
    if (!curveHasPoints(curve)) return null;
    return curve.normalForce.map((nf, i) => ({
      force: -(Number(nf) * N_TO_KN),
      moment: Number(curve.moment[i] ?? 0) * NM_TO_KNM,
    }));
  };

  // Build per-lco design points from code check results
  interface LcoPoint {
    svgPos: [number, number]; // [moment_svg, force_svg]
    utilisation: number;
    lcName: string;
    mKNm: number;
    nKN: number;
    isSelected: boolean;
    // Whether mKNm is the design moment MEd (M + Madd) rather than the moment from load alone -
    // it decides what the label may call the value.
    isDesignMoment: boolean;
  }
  const lcoPoints: LcoPoint[] = [];

  for (const lcoGuid of Object.keys(props.results.design)) {
    // A marker belongs on the curve its own load combination was checked against, so only the type
    // whose curve is on screen may contribute one. This also drops the "envelope" pseudo
    // combination the results carry for an envelope selection: it is no load combination, has no
    // type, and so no curve of its own.
    if (selection === null || lcombTypes.get(lcoGuid) !== selection.markerType) continue;

    const elemDesignMap = props.results.design[lcoGuid];
    if (!elemDesignMap) continue;

    // Use the first element's design results
    const firstElemResults = Object.values(elemDesignMap)[0];
    if (!firstElemResults || firstElemResults.length === 0) continue;

    // Find the moment control with highest utilisation for the direction being drawn. Fall back to
    // the other direction's controls when the engine reports none for this one, so the point is
    // still placed rather than dropped - but the fallback only locates the point, it never lends it
    // its utilisation (see below).
    const primary = props.alternate ? MOMENT_CONTROLS_M2 : MOMENT_CONTROLS_M1;
    const secondary = props.alternate ? MOMENT_CONTROLS_M1 : MOMENT_CONTROLS_M2;
    const primaryDominant = findDominantControl(firstElemResults, primary);
    const dominant = primaryDominant ?? findDominantControl(firstElemResults, secondary);
    if (!dominant) continue;

    // Only a control for the direction being drawn reports the utilisation of THIS diagram's
    // moment. When the engine reports none, the plane was never checked - a column loaded in one
    // plane only gets no M1 control at all - and the marker says 0 %. Carrying the other plane's
    // figure across would label the point with a capacity ratio it was never measured against,
    // which is what made both panels read the same percentage.
    const utilisation = primaryDominant ? primaryDominant.utilisation : 0;

    // Only the control for the direction being drawn reports a moment about this diagram's axis -
    // a fallback control from the other direction would put its own axis' MEd on the wrong chart,
    // so that case keeps using section forces.
    const designPoint = primaryDominant ? designPointFromControl(primaryDominant) : null;

    let mKNm: number;
    let nKN: number;

    if (designPoint) {
      mKNm = designPoint.moment * NM_TO_KNM;
      nKN = designPoint.force * N_TO_KN;
    } else {
      // Look up section forces at the dominant control's position
      const elemForcesForLco = props.results.elementForces[lcoGuid];
      if (!elemForcesForLco) continue;
      const firstElemForces = Object.values(elemForcesForLco)[0];
      if (!firstElemForces || firstElemForces.length === 0) continue;

      const closest = findClosestForce(firstElemForces, dominant.pos);
      if (!closest) continue;

      // XZ view → M1, XY view → M2
      mKNm = (props.alternate ? closest.M2.max : closest.M1.max) * NM_TO_KNM;
      nKN = closest.N.max * N_TO_KN;
    }

    lcoPoints.push({
      svgPos: [mKNm * moment_scale, nKN * force_scale],
      utilisation,
      lcName: dominant.lco,
      isDesignMoment: designPoint !== null,
      mKNm,
      nKN,
      isSelected: props.selectedLcombGuid === lcoGuid,
    });
  }

  const curve = buildCurveFromReal(props.alternate ? firstResult?.zAxis : firstResult?.yAxis);
  const otherCurve = buildCurveFromReal(props.alternate ? firstResult?.yAxis : firstResult?.zAxis);
  const biaxialCurve = allowBiaxial
    ? buildCurveFromReal(props.alternate ? firstResult?.zAxisBiaxial : firstResult?.yAxisBiaxial)
    : null;
  const hasRealData = curve !== null && curve !== undefined && curve.length > 0;
  const hasBiaxialData = biaxialCurve !== null && biaxialCurve !== undefined && biaxialCurve.length > 0;

  // Axis extents — use max bounding box of ALL curves so XY and XZ share the same scale
  const allCurves = [curve, otherCurve, biaxialCurve].filter(
    (c): c is Data[] => c !== null && c !== undefined && c.length > 0,
  );
  const allPoints = allCurves.flat();
  const force_min = allPoints.length > 0 ? Math.min(...allPoints.map((p) => p.force)) : -1000;
  const force_max = allPoints.length > 0 ? Math.max(...allPoints.map((p) => p.force)) : 0;
  const moment_min = allPoints.length > 0 ? Math.min(...allPoints.map((p) => p.moment)) : -500;
  const moment_max = allPoints.length > 0 ? Math.max(...allPoints.map((p) => p.moment)) : 500;

  // draw axis
  {
    g.drawLine(
      [0, (force_min - force_neg_extra) * force_scale],
      [0, (force_max + force_pos_extra) * force_scale],
      interaction_zero_line,
    );
    g.drawLine(
      [(moment_min - moment_extra) * moment_scale, 0],
      [(moment_max + moment_extra) * moment_scale, 0],
      interaction_zero_line,
    );

    g.drawText([0, (force_min - force_neg_extra) * force_scale], "N (kN)", axis_label_style, {
      vertOffset: -3,
      vertAlign: "bottom",
      horiAlign: "center",
    });
    g.drawText(
      [(moment_max + moment_extra) * moment_scale, 0],
      props.alternate ? "Mz (kNm)" : "My (kNm)",
      axis_label_style,
      {
        rotation: "upward",
        horiOffset: 3,
        horiAlign: "center",
        vertAlign: "top",
      },
    );

    // moment axis ticks (horizontal)
    const moment_step = nice_step(moment_max - moment_min);
    for (let m = Math.ceil(moment_min / moment_step) * moment_step; m <= moment_max; m += moment_step) {
      if (Math.abs(m) < moment_step * 0.01) continue;
      g.drawSymbol([m * moment_scale, 0], "axis-tick", interaction_tick_style, interaction_tick_vertical);
      g.drawText([m * moment_scale, 0], String(Math.round(m)), interaction_tick_label, {
        horiAlign: "center",
        vertAlign: "top",
        vertOffset: tick_offset,
      });
    }

    // force axis ticks (vertical)
    const force_step = nice_step(force_max - force_min);
    for (let f = Math.ceil(force_min / force_step) * force_step; f <= force_max; f += force_step) {
      if (Math.abs(f) < force_step * 0.01) continue;
      g.drawSymbol([0, f * force_scale], "axis-tick", interaction_tick_style, interaction_tick_horizontal);
      g.drawText([0, f * force_scale], String(Math.round(f)), interaction_tick_label, {
        horiAlign: "right",
        vertAlign: "middle",
        horiOffset: -tick_offset,
      });
    }
  }

  // draw curve (only when data is available) — Catmull-Rom spline for smooth rendering
  if (hasRealData) {
    const pts: [number, number][] = curve!.map((p) => [p.moment * moment_scale, p.force * force_scale]);

    const path = new PathBuilder();
    path.moveTo(pts[0]);

    if (pts.length === 2) {
      path.drawLineTo(pts[1]);
    } else {
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i === 0 ? 0 : i - 1];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2 < pts.length ? i + 2 : pts.length - 1];

        // Catmull-Rom → cubic Bezier control points
        const cp1: [number, number] = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const cp2: [number, number] = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];

        path.drawCubicBezier(p2, { start: cp1, end: cp2 });
      }
    }

    g.drawPath(curve_line, path);

    if (accent) {
      // Name the curve where it reaches furthest out in moment: the widest point of the boundary,
      // which is clear of both axes and of the ticks, so the caption reads as belonging to the
      // curve rather than to a load combination marker inside it.
      const widest = pts.reduce((best, p) => (p[0] > best[0] ? p : best), pts[0]);
      g.drawText(widest, t(accent.captionKey, accent.captionFallback), interaction_curve_caption(accent.color), {
        horiAlign: "left",
        horiOffset: 8,
        vertAlign: "bottom",
        vertOffset: -4,
      });
    }

    if (show_curve_points) {
      for (let i = 0; i < pts.length; i++) {
        g.drawSymbol(pts[i], "interaction-point", interaction_point_style);
      }
    }
  }

  // draw biaxial curve (same color, dashed line)
  if (hasBiaxialData) {
    const pts: [number, number][] = biaxialCurve!.map((p) => [p.moment * moment_scale, p.force * force_scale]);

    const path = new PathBuilder();
    path.moveTo(pts[0]);

    if (pts.length === 2) {
      path.drawLineTo(pts[1]);
    } else {
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i === 0 ? 0 : i - 1];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2 < pts.length ? i + 2 : pts.length - 1];

        const cp1: [number, number] = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const cp2: [number, number] = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];

        path.drawCubicBezier(p2, { start: cp1, end: cp2 });
      }
    }

    g.drawPath({ ...curve_line, dashed: true }, path);
  }

  // draw one point per load combination
  const drawPoints = (drawSelected: boolean) => {
    for (const pt of lcoPoints) {
      if (pt.isSelected !== drawSelected) continue;
      const ok = pt.utilisation <= 1.0;
      const baseStyle = ok ? interaction_style_ok : interaction_style_fail;
      const style = pt.isSelected ? { ...baseStyle, ...interaction_style_extended_selected } : baseStyle;
      const utilPct = (pt.utilisation * 100).toFixed(1) + "%";
      const kn = `${Math.round(pt.nKN)} kN`;
      const mom = `${Math.round(pt.mKNm)} kNm`;
      // Only the design point carries the additional moment; a point placed from section forces is
      // the moment from load and must not be labelled MEd.
      const momName = pt.isDesignMoment ? (props.alternate ? "MEd,z" : "MEd,y") : props.alternate ? "Mz" : "My";
      const symbol: ArrowId = pt.isSelected ? "interaction-loadcase-point-selected" : "interaction-loadcase-point";
      g.drawSymbol(pt.svgPos, symbol, style);
      if (pt.isSelected) {
        g.drawText(
          pt.svgPos,
          <>
            <TextLine>
              {pt.lcName} ({utilPct})
            </TextLine>
            <TextLine>
              N = {kn}, {momName} = {mom}
            </TextLine>
          </>,
          interaction_selected_lcomb_text,
          {
            horiAlign: "left",
            horiOffset: 14,
            vertOffset: -12, // -12 vertical offset is a hack since alignment isn't considerd whith multiline texts
            vertAlign: "bottom",
          },
        );
      }
    }
  };
  drawPoints(false);
  drawPoints(true);

  const items = g.toDrawables();
  return <Svg<ArrowId> items={items} markers={ColumnMarkers()} isDev={isDev} autoZoom={autoZoom} />;
};
